/* RESPONSIBLE TEAM: team-standalone */

import Platform from './platform';
import type { IntegrationType } from '../constants';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type AppService from 'embercom/services/app-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import {
  TicketField,
  UserField,
  OrganizationField,
  type TicketFieldResponse,
  type UserFieldResponse,
  type OrganizationFieldResponse,
  type FieldType,
} from '../data-fields/common';

/**
 * Abstract base class for standardized platforms (HubSpot, Freshdesk) that use
 * the newer standardized Integration endpoints.
 */
export default abstract class StandardizedPlatform extends Platform {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;

  @tracked private loadedTicketFields: TicketField[] = [];
  @tracked private loadedUserFields: UserField[] = [];
  @tracked private loadedOrganizationFields: OrganizationField[] = [];

  /**
   * Each platform must provide its specific IntegrationTypes
   */
  protected abstract get integrationTypes(): IntegrationType[];

  async loadRequiredData() {
    await this.loadDataFields();
  }

  async loadDataFields() {
    let appId = this.appService.app.id;

    // Fetch data fields from all integration types concurrently
    let fetchPromises = this.integrationTypes.map(async (integrationType) => {
      let integration = this.finStandaloneService.standaloneIntegrations.find(
        (int) => int.integrationType === integrationType,
      );

      if (!integration?.id) {
        return { user: [], conversation: [], company: [] };
      }

      let url = `/ember/standalone/integrations/${integrationType}/${integration.id}/available_fields?app_id=${appId}`;

      try {
        let response = await request(url);
        let data = await response.json();
        return {
          user: data.user || [],
          conversation: data.conversation || [],
          company: data.company || [],
        };
      } catch (error) {
        console.error(
          `Failed to load ${this.platformKey} data fields for ${integrationType}:`,
          error,
        );
        return { user: [], conversation: [], company: [] };
      }
    });

    let results = await Promise.all(fetchPromises);

    let allUserFields = results.flatMap((result) => result.user);
    let allTicketFields = results.flatMap((result) => result.conversation);
    let allOrganizationFields = results.flatMap((result) => result.company);

    // Some platforms may use the same field mappings across all its integrations.
    // In this case, we should deduplicate the fields by ID before mapping to Ember field types.
    this.loadedUserFields = this.uniqueById(allUserFields).map((attr: any) =>
      this.mapToUserField(attr),
    );
    this.loadedTicketFields = this.uniqueById(allTicketFields).map((attr: any) =>
      this.mapToTicketField(attr),
    );
    this.loadedOrganizationFields = this.uniqueById(allOrganizationFields).map((attr: any) =>
      this.mapToOrganizationField(attr),
    );
  }

  private uniqueById(items: any[]): any[] {
    let seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }

  private mapToUserField(attr: any): UserField {
    let response: UserFieldResponse = {
      id: attr.id,
      name: attr.name,
      description: attr.intercom_cda?.description || undefined,
      type: attr.type as FieldType,
      required: false,
      is_mappable: true,
    };

    if (attr.intercom_cda) {
      response.intercom_cda = {
        identifier: attr.intercom_cda.identifier,
        type: attr.intercom_cda.type,
      };
    }

    return new UserField(response);
  }

  private mapToTicketField(attr: any): TicketField {
    let response: TicketFieldResponse = {
      id: attr.id,
      name: attr.name,
      description: attr.intercom_cvda?.description || undefined,
      type: attr.type as FieldType,
      required: false,
    };

    if (attr.intercom_cvda) {
      response.intercom_cvda = {
        id: attr.intercom_cvda.id,
      };
    }

    return new TicketField(response);
  }

  private mapToOrganizationField(attr: any): OrganizationField {
    let response: OrganizationFieldResponse = {
      id: attr.id,
      name: attr.name,
      description: attr.intercom_cda?.description || undefined,
      type: attr.type as FieldType,
      required: false,
    };

    if (attr.intercom_cda) {
      response.intercom_cda = {
        identifier: attr.intercom_cda.identifier,
        type: attr.intercom_cda.type,
      };
    }

    return new OrganizationField(response);
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return this.loadedUserFields.any(
      (userField) =>
        userField.intercom_cda?.identifier === attributeIdentifier ||
        userField.intercom_sda?.identifier === attributeIdentifier,
    );
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    return this.loadedOrganizationFields.any(
      (organizationField) =>
        `company.${organizationField.intercom_cda?.identifier}` === attributeIdentifier ||
        `company.${organizationField.intercom_sda?.identifier}` === attributeIdentifier,
    );
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return this.loadedTicketFields.any(
      (ticketField) => `conversation.${ticketField.intercom_cvda?.id}` === attributeIdentifier,
    );
  }

  get ticketFields() {
    return this.loadedTicketFields;
  }

  get userFields() {
    return this.loadedUserFields;
  }

  get organizationFields() {
    return this.loadedOrganizationFields;
  }
}
